import React, { useMemo, useState } from 'react';
import { Button, Input, Text, makeStyles, shorthands, tokens } from '@fluentui/react-components';
import { Dismiss24Regular, Search24Regular } from '@fluentui/react-icons';
import ExploreGPTBanner from '../../../../assets/ExploreGPTBanner.png';
import { NexusAgent } from '../../../../domain/agents/models/Agent';
import { AlwaysOnScrollBarStyles, Breakpoints, customColors, customFonts } from '../../../../styles';
import { useExploreAgentsPage } from '../hooks/useExploreAgentsPage';
import AgentTile from './AgentTile';
import AgentOverviewDialog from '../../../AgentOverview/components/AgentOverviewDialog';

const useStyles = makeStyles({
    root: {
        width: '100%',
        height: '90%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        backgroundColor: customColors.protivitiOffWhite,
        ...shorthands.padding('72px', '12px', '20px', '12px'),
        ...shorthands.overflow('auto'),
        '@media (max-width: 576px)': {
            paddingLeft: '0px',
            paddingRight: '0px',
        },
        ...AlwaysOnScrollBarStyles,
    },
    header: {
        width: '85%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundImage: `url(${ExploreGPTBanner})`,
        padding: '20px 20px 20px 20px',
        backgroundRepeat: 'no-repeat',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        gap: '10px',
        marginBottom: '20px',
        '@media (max-width: 1500px)': {
            backgroundSize: '100% 100%',
        },
        ...shorthands.borderRadius('15px'),
    },
    title: {
        fontSize: '2em',
        marginTop: '10px',
        textAlign: 'center',
        lineHeight: '24px',
        color: customColors.white,
        '@media (max-width: 576px)': {
            fontSize: '1.6em',
        },
    },
    subtitle: {
        fontSize: '1.3em',
        textAlign: 'center',
        color: customColors.white,
        '@media (max-width: 576px)': {
            fontSize: '1.2em',
        },
    },
    searchBar: {
        width: '50%',
        minWidth: '550px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        marginBottom: '10px',
        ...Breakpoints.small({
            minWidth: '350px',
        }),
        '@media (max-width: 576px)': {
            minWidth: '250px',
        },
    },
    searchBarInput: {
        width: '100%',
        height: '50px',
    },
    clearButton: {
        minWidth: 'auto',
        padding: '0',
        backgroundColor: 'transparent',
        border: 'none',
        cursor: 'pointer',
        '&:hover': {
            backgroundColor: 'transparent',
        },
    },
    content: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: '85%',
    },
    section: {
        textAlign: 'left',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: tokens.colorNeutralBackground1,
        ...shorthands.margin('8px', '0', '12px', '0'),
        padding: '0px 10px 18px 10px',
        borderRadius: '15px',
    },
    sectionHeader: {
        ...shorthands.padding(tokens.spacingVerticalM, tokens.spacingHorizontalL),
        textAlign: 'left',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
    },
    sectionTitle: {
        marginBottom: '2px',
        fontSize: '25px',
        fontWeight: '500',
        color: customColors.summarizeButtonDarker,
        fontFamily: customFonts.TitleFont,
    },
    sectionSubtitle: {
        fontSize: '18px',
        fontWeight: '500',
        color: customColors.headerLightGray,
        fontFamily: customFonts.TitleFont,
    },
    agentList: {
        display: 'flex',
        justifyContent: 'space-evenly',
        flexWrap: 'wrap',
        width: '100%',
        height: '100%',
        ...shorthands.overflow('auto'),
        ...shorthands.padding('16px', '0', '32px', '0'),
    },
    disclaimer: {
        color: 'red',
        padding: '8px 0px 0px 0px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        fontSize: tokens.fontSizeBase300,
    },
    emptyState: {
        width: '100%',
        textAlign: 'center',
        padding: '40px 20px 48px',
        color: customColors.darkNavy,
        fontFamily: customFonts.Lato,
    },
});

const ExploreAgentsPage: React.FC = () => {
    const styles = useStyles();
    const { agents, chatCount, allowedChatCount, isMaxChatsReached, createAgentChat } = useExploreAgentsPage();
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedAgent, setSelectedAgent] = useState<NexusAgent | null>(null);

    const filteredAgents: NexusAgent[] = useMemo(() => {
        const normalizedQuery = searchQuery.trim().toLowerCase();

        if (!normalizedQuery) {
            return agents;
        }

        return agents.filter((agent) => {
            const searchableText = [agent.name, agent.description, agent.shortDescription ?? '']
                .join(' ')
                .toLowerCase();

            return searchableText.includes(normalizedQuery);
        });
    }, [agents, searchQuery]);

    const handleClearSearch = () => {
        setSearchQuery('');
    };

    const emptyStateText =
        agents.length === 0 ? 'No Nexus agents are available yet.' : 'No Nexus agents matched your search.';

    return (
        <div className={styles.root}>
            <div className={styles.header}>
                <Text className={styles.title}>Protiviti Specialized Agent Library</Text>
                <Text as="p" className={styles.subtitle}>
                    Explore Protiviti&apos;s specialized Agents built upon our intellectual property
                </Text>

                <div className={styles.searchBar}>
                    <Input
                        className={styles.searchBarInput}
                        contentBefore={<Search24Regular />}
                        contentAfter={
                            searchQuery && (
                                <Button
                                    appearance="transparent"
                                    className={styles.clearButton}
                                    icon={<Dismiss24Regular />}
                                    onClick={handleClearSearch}
                                    aria-label="Clear search"
                                />
                            )
                        }
                        placeholder="Search Nexus Agents"
                        value={searchQuery}
                        onChange={(event) => setSearchQuery(event.target.value)}
                    />
                </div>
            </div>

            <div className={styles.content}>
                <div className={styles.section}>
                    <div className={styles.sectionHeader}>
                        <Text as="h1" className={styles.sectionTitle}>
                            Nexus Agents
                        </Text>
                        {isMaxChatsReached && allowedChatCount !== null && (
                            <Text className={styles.disclaimer}>
                                You have reached the maximum number of chat sessions ({chatCount}/{allowedChatCount}).
                                Please delete one to create a new one.
                            </Text>
                        )}
                    </div>

                    <div className={styles.agentList}>
                        {filteredAgents.length > 0 ? (
                            filteredAgents.map((agent) => (
                                <AgentTile
                                    key={agent.agentId}
                                    agent={agent}
                                    isDisabled={isMaxChatsReached}
                                    onOpenChat={createAgentChat}
                                    onLearnMore={setSelectedAgent}
                                />
                            ))
                        ) : (
                            <div className={styles.emptyState}>
                                <Text>{emptyStateText}</Text>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <AgentOverviewDialog agent={selectedAgent} setIsDialogOpen={setSelectedAgent} />
        </div>
    );
};

export default ExploreAgentsPage;
